import * as Services from '../../../services/index.js';
import * as dates from '@simplybenefits/sb-dates';
import { hasPersonalInfoChanges, returnClassID } from './parseEmployeeData.js';
import * as localizationMaps from '../../../localization/maps.js';

/**
 * Validates the form data for an employee update, performing asynchronous input validation
 * using services and checking for specific changes between changedData and initialData.
 * 
 * @param {Object} changedData - Updated employee data to validate
 * @param {Object} initialData - Initial employee data for comparison
 * @returns {Promise<Object|boolean>} If errors are found, returns an object with validation errors;
 *                                    otherwise, returns false indicating no errors.
 */
const validateForm = async (changedData, initialData, isEdit, isAdmin, classData, isStudent = false, isMasterProfile = false) => {
  const errors = {}; // Object to store validation errors

  const {
    firstname,
    lastname,
    email,
    birthdate,
    className,
    divisionName,
    hireDate,
    gender,
    title,
    coverageType,
    province,
    addressLineOne,
    city,
    postalCode,
    earningsType,
    salary,
    hourlyWage,
    hoursPerWeek,
    waiveEligibilityPeriod,
    phone,
    maritlStat,
    studentID,
    studyStartDate,
    status
  } = changedData;
  errors.firstnameError = await Services.validateInput('firstname', firstname?.trim());
  errors.lastnameError = await Services.validateInput('lastname', lastname?.trim());
  errors.emailError = await Services.validateInput('email', email?.trim());
  errors.phoneError = isEdit && isAdmin && phone ? await Services.validateInput('phone', phone) : false;
  errors.maritlStatError = isEdit && isAdmin && maritlStat ? await Services.validateInput('maritlStat', maritlStat) : false;
  errors.birthdateError = await Services.validateInput('birthdate', birthdate);
  errors.classNameError = await Services.validateInput('className', className);
  errors.divisionNameError = await Services.validateInput('divisionName', divisionName);
  errors.genderError = await Services.validateInput('gender', gender);
  errors.coverageTypeError = await Services.validateInput('coverageType', coverageType);
  errors.provinceError = await Services.validateInput('province', province);
  errors.waiveEligibilityPeriodError = await Services.validateInput('waiveEligibilityPeriod', waiveEligibilityPeriod);
  errors.hireDateError = null;
  errors.titleError = null;
  errors.earningsTypeError = null;
  errors.salaryError = null;
  errors.hourlyWageError = null;
  errors.hoursPerWeekError = null;
  if (isStudent) {
    errors.studentIDError = await Services.validateInput('checkEmpty', studentID);
    errors.studyStartDateError = await Services.validateInput('studyStartDate', studyStartDate);
    errors.addressLineOneError = await Services.validateInput('addressLineOne', addressLineOne?.trim());
    errors.cityError = await Services.validateInput('city', city?.trim());
    errors.postalCodeError = await Services.validateInput('postalCode', postalCode?.trim());
    errors.phoneError = await Services.validateInput('phone', phone);
    errors.maritlStatError = await Services.validateInput('maritlStat', maritlStat);
  } else {
    errors.titleError = await Services.validateInput('title', title?.trim());
    errors.hireDateError = await Services.validateInput('hireDate', hireDate);
    errors.earningsTypeError = await Services.validateInput('earningsType', earningsType);
    errors.hoursPerWeekError = await Services.validateInput('hoursPerWeek', hoursPerWeek);
    errors.salaryError = localizationMaps.toEnglish('earningsTypes', earningsType) === 'Salary' ? await Services.validateInput('salary', salary) : null;
    errors.hourlyWageError = localizationMaps.toEnglish('earningsTypes', earningsType) === 'Hourly' ? await Services.validateInput('hourlyWage', hourlyWage) : null;
    isEdit && status !== 'Pending' && status !== 'Late Applicant' && status !== undefined ? errors.addressLineOneError = await Services.validateInput('addressLineOne', addressLineOne?.trim()) : null;
    isEdit && status !== 'Pending' && status !== 'Late Applicant' && status !== undefined ? errors.cityError = await Services.validateInput('city', city?.trim()) : null;
    isEdit && status !== 'Pending' && status !== 'Late Applicant' && status !== undefined ? errors.postalCodeError = await Services.validateInput('postalCode', postalCode?.trim()) : null;
  }
  if (isEdit && isAdmin) {
    const changesInPersonalInfo = hasPersonalInfoChanges(changedData, initialData);
    const changesInClassOrHireDate = returnClassID(classData, className) !== initialData.classID || !isStudent && dates.getUTC(hireDate) !== initialData.hireDate // do not check for hire date change for student groups, as this field does not apply for students
    ;
    if (changesInPersonalInfo && changesInClassOrHireDate && waiveEligibilityPeriod) {
      errors.employeeDetailsPersonalDetailsError = true;
    }
  }

  // only perform this validation if we are editing the user from the master profile
  if (isMasterProfile) {
    if (['PENDING', 'ACTIVE', 'APPROVED LEAVE'].includes(changedData.status.toUpperCase()) && changedData.benefitsActive === 0) {
      errors.benefitsActiveError = 'Benefits can not be Inactive when the member is in a Pending, Active or Approved Leave status.';
    } else if (['TERMINATED'].includes(changedData.status.toUpperCase()) && changedData.benefitsActive === 1) {
      errors.benefitsActiveError = 'Benefits can not be Active when the member is in a Terminated status.';
    }
  }

  // Return the errors if there are any errors, otherwise false
  const hasErrors = Object.values(errors).some(Boolean);
  return hasErrors ? errors : false;
};
export default validateForm;